import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight,
  Target,
  Eye,
  Shield,
  Lightbulb,
  Users,
  Globe,
  Linkedin,
  Twitter,
} from 'lucide-react';

const values = [
  {
    icon: Shield,
    title: 'Trust',
    description: 'We build systems that deliver verified, auditable sustainability data.',
  },
  {
    icon: Eye,
    title: 'Evidence',
    description: 'Every claim backed by transparent, traceable evidence trails.',
  },
  {
    icon: Globe,
    title: 'Transparency',
    description: 'Open methodologies and clear documentation for all stakeholders.',
  },
  {
    icon: Users,
    title: 'Interoperability',
    description: 'Standards-based solutions that integrate across ecosystems.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'Pioneering new approaches to sustainability data and finance.',
  },
];

const team = [
  {
    name: 'John Chen',
    role: 'CEO & Co-Founder',
    bio: 'Former sustainability data lead at a Fortune 500 company. 15+ years in ESG data systems.',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80',
    linkedin: '#',
    twitter: '#',
  },
  {
    name: 'Sarah Martinez',
    role: 'CTO & Co-Founder',
    bio: 'Previously led data engineering at a major cloud provider. Expert in distributed systems.',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80',
    linkedin: '#',
    twitter: '#',
  },
  {
    name: 'Michael Wong',
    role: 'Chief Data Officer',
    bio: 'Former data science director. Specializes in sustainability analytics and ML.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80',
    linkedin: '#',
    twitter: '#',
  },
  {
    name: 'Emily Johnson',
    role: 'VP, Product',
    bio: 'Product leader with experience in enterprise sustainability software.',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80',
    linkedin: '#',
    twitter: '#',
  },
];

const milestones = [
  { year: '2022', event: 'Asia-wide research on sustainability credibility engine gap' },
  { year: '2023', event: 'Embarked on designing and developing AI-powered Sustainability Data Platform' },
  { year: '2024', event: 'Registered SusTek as Pte Limited company in Singapore' },
  { year: '2025', event: 'Launch our first Industry Association Sustainability Report' },
  { year: '2026', event: 'Started our data-first credibility model for ESG / Materiality KPIs' },
];

export const About = () => {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <Badge variant="outline" className="mb-6 border-teal/30 text-teal bg-teal/10">
              About SusTek
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              From ESG Platform to{' '}
              <span className="text-teal">Credibility Infrastructure</span>
            </h1>
            <p className="text-xl text-white/70 mb-8">
              We started as an ESG SaaS platform. Today, we engineer trusted sustainability
              data systems and credibility engines for reporting and sustainability finance.
            </p>
          </div>
        </div>
      </section>

      {/* Story / Vision & Mission */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <Badge variant="outline" className="mb-4">Our Vision</Badge>
              <h2 className="text-3xl font-bold text-foreground mb-6">
                A World Where Sustainability Claims Are Trusted
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                We envision a future where every sustainability claim is backed by
                verifiable, finance-grade evidence—enabling capital to flow confidently
                toward genuine environmental impact.
              </p>
              <div className="p-6 rounded-xl bg-teal/5 border border-teal/20">
                <div className="flex items-start gap-4">
                  <Target className="h-8 w-8 text-teal flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Our Mission</h3>
                    <p className="text-muted-foreground">
                      To engineer trusted sustainability data and convert it into
                      finance-grade credibility—bridging the gap between environmental
                      action and sustainable finance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
                alt="Modern Corporate Building"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="mb-4">Our Values</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Principles That Guide Us
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="text-center border-border/50 bg-card hover-lift">
                <CardContent className="pt-6">
                  <div className="w-14 h-14 mx-auto rounded-xl bg-teal/10 text-teal flex items-center justify-center mb-4">
                    <value.icon className="h-7 w-7" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="mb-4">Our Journey</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Building the Future of Sustainability Data
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-border -translate-x-1/2 hidden lg:block" />
            <div className="space-y-8 lg:space-y-0">
              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`relative lg:flex items-center ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:pr-12 lg:text-right' : 'lg:pl-12'}`}>
                    <Card className="inline-block border-border/50 bg-card">
                      <CardContent className="p-4">
                        <span className="text-teal font-bold">{milestone.year}</span>
                        <p className="text-muted-foreground mt-1">{milestone.event}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-teal border-4 border-background" />
                  <div className="lg:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team - Hidden for now */}
      {/* 
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="mb-4">Leadership</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Meet Our Team
            </h2>
            <p className="text-lg text-muted-foreground">
              Industry veterans with deep expertise in sustainability, data engineering,
              and enterprise software.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <Card key={index} className="overflow-hidden border-border/50 bg-card hover-lift">
                <div className="aspect-square relative overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">{member.name}</CardTitle>
                  <CardDescription className="text-teal font-medium">
                    {member.role}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">{member.bio}</p>
                  <div className="flex gap-3">
                    <a
                      href={member.linkedin}
                      className="text-muted-foreground hover:text-teal transition-colors"
                      aria-label={member.name + " LinkedIn"}
                    >
                      <Linkedin className="h-5 w-5" />
                    </a>
                    <a
                      href={member.twitter}
                      className="text-muted-foreground hover:text-teal transition-colors"
                      aria-label={member.name + " Twitter"}
                    >
                      <Twitter className="h-5 w-5" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Careers CTA */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-primary text-primary-foreground border-0">
            <CardContent className="p-8 lg:p-12">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                    Join Our Mission
                  </h2>
                  <p className="text-lg text-primary-foreground/80">
                    We&apos;re building the credibility infrastructure for sustainability finance.
                    Come help us make it happen.
                  </p>
                </div>
                <Link to="/careers">
                  <Button variant="secondary" size="lg" className="group text-secondary-foreground">
                    View Open Positions
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default About;
